import { useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Calendar, Users, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const toISO = (d) => d.toISOString().slice(0, 10);
const today = () => toISO(new Date());

function startOfWeekMon(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return toISO(d);
}
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return toISO(d); };
const startOfMonth = (iso) => { const d = new Date(iso); d.setDate(1); return toISO(d); };
const endOfMonth = (iso) => { const d = new Date(iso); d.setMonth(d.getMonth() + 1); d.setDate(0); return toISO(d); };

export default function ReportClientSummary() {
  const [period, setPeriod] = useState("weekly");
  const [date, setDate] = useState(today());
  const [customFrom, setCustomFrom] = useState(today());
  const [customTo, setCustomTo] = useState(today());
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [includeWorkers, setIncludeWorkers] = useState(false);
  const [workerKind, setWorkerKind] = useState("both");

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || [])).catch(e => toast.error(formatApiError(e)));
  }, []);

  const { dateFrom, dateTo, label } = useMemo(() => {
    if (period === "daily") return { dateFrom: date, dateTo: date, label: date };
    if (period === "weekly") {
      const m = startOfWeekMon(date); const s = addDays(m, 6);
      return { dateFrom: m, dateTo: s, label: `${m} → ${s}` };
    }
    if (period === "monthly") {
      const m = startOfMonth(date); const e = endOfMonth(date);
      return { dateFrom: m, dateTo: e, label: `${m} → ${e}` };
    }
    return { dateFrom: customFrom, dateTo: customTo, label: `${customFrom} → ${customTo}` };
  }, [period, date, customFrom, customTo]);

  const downloadHref = useMemo(() => {
    if (!clientId) return null;
    const p = new URLSearchParams({
      client_id: clientId, period, date_from: dateFrom, date_to: dateTo,
      include_workers: includeWorkers ? "true" : "false",
      worker_kind: workerKind,
    });
    return fileUrl(`/reports/client-summary.pdf?${p.toString()}`);
  }, [clientId, period, dateFrom, dateTo, includeWorkers, workerKind]);

  const excelHref = useMemo(() => {
    if (!clientId) return null;
    const p = new URLSearchParams({
      client_id: clientId, period, date_from: dateFrom, date_to: dateTo,
      include_workers: includeWorkers ? "true" : "false",
      worker_kind: workerKind,
    });
    return fileUrl(`/reports/client-summary.xlsx?${p.toString()}`);
  }, [clientId, period, dateFrom, dateTo, includeWorkers, workerKind]);

  return (
    <div data-testid="report-client-summary-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · Client</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Service report (for client)</h1>
        <p className="text-sm text-slate-600 mt-1">A clean, sendable PDF summarising what we delivered for a client in the chosen period. Optionally include the subcontractors / self-employed drivers used.</p>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="cs-client" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder="Pick a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="cs-period" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {period !== "custom" ? (
            <div>
              <Label className="text-xs text-slate-600">
                {period === "monthly" ? "Pick any day in the month" : period === "weekly" ? "Pick any day in the week" : "Date"}
              </Label>
              <Input
                data-testid="cs-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-600">From</Label>
                <Input data-testid="cs-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To</Label>
                <Input data-testid="cs-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-600">&nbsp;</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Button
                asChild={!!clientId}
                disabled={!clientId}
                data-testid="cs-download"
                className="rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300"
              >
                {clientId ? (
                  <a href={downloadHref} download rel="noopener">
                    <Download className="w-4 h-4 mr-1.5" /> PDF
                  </a>
                ) : (
                  <span><Download className="w-4 h-4 mr-1.5 inline" /> PDF</span>
                )}
              </Button>
              <Button
                asChild={!!clientId}
                disabled={!clientId}
                variant="outline"
                data-testid="cs-download-xlsx"
                className="rounded-sm border-slate-300 hover:bg-slate-100 disabled:opacity-50"
              >
                {clientId ? (
                  <a href={excelHref} download rel="noopener">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
                  </a>
                ) : (
                  <span><FileSpreadsheet className="w-4 h-4 mr-1.5 inline" /> Excel</span>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="cs-toggle-workers"
              checked={includeWorkers}
              onCheckedChange={setIncludeWorkers}
            />
            <div>
              <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Show sub / driver names
              </div>
              <div className="text-xs text-slate-500">Off = anonymous client-safe report. On = adds the people / firms used.</div>
            </div>
          </div>

          <div className={includeWorkers ? "" : "opacity-50 pointer-events-none"}>
            <Label className="text-xs text-slate-600">Include resources of type</Label>
            <Select value={workerKind} onValueChange={setWorkerKind}>
              <SelectTrigger data-testid="cs-worker-kind" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Subcontractors + self-employed</SelectItem>
                <SelectItem value="sub">Subcontractors only</SelectItem>
                <SelectItem value="self">Self-employed drivers only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="text-xs text-slate-500 inline-flex items-center gap-2 md:justify-end">
            <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{label}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
